// Import: a Lichess study or a PGN becomes a repertoire — or replaces the
// lines of one. Every variation becomes part of the line, and the comments
// come with it.

import { useEffect, useState } from "react";
import { RequestError, isSignedOut, sendJson } from "../app/api.ts";
import { refreshDrillBadge } from "../app/badge.ts";
import { cap, fmtCount } from "../app/format.ts";
import { Icon } from "../app/icons.tsx";
import { goBack, replaceWith } from "../app/router.ts";
import { BackButton, useShell, type ScreenProps } from "../app/App.tsx";
import { Loading, Notice, ScreenHead, Seg } from "../app/ui.tsx";

interface Preview {
  chapters?: { name?: string; moves: number }[];
  positions: number;
  orientation?: "white" | "black" | null;
  guessed_color?: "white" | "black";
}

/** What went wrong, and where: the chapter and the move, said once. */
function whereText(err: unknown): string {
  const d = err instanceof RequestError ? err.data || {} : {};
  const message = String(err instanceof Error ? err.message : err);
  const where: string[] = [];
  if (d.chapter != null || d.chapter_name) {
    where.push([d.chapter != null ? `Chapter ${d.chapter}` : "Chapter", d.chapter_name ? `“${d.chapter_name}”` : ""].filter(Boolean).join(" "));
  }
  if (d.move) where.push(`move ${d.move}`);
  // The server's message may already say where; say it once.
  if (d.move && message.includes(d.move)) return message.charAt(0).toUpperCase() + message.slice(1);
  return (where.length ? where.join(", ") + ": " : "") + message;
}

export function ImportScreen({ query }: ScreenProps) {
  const replaceId = query.get("replace");
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState("");
  const [preview, setPreview] = useState<{ data: Preview; text: string } | null>(null);
  const meta = { title: replaceId ? "Re-import" : "Import", nav: "openings", back: replaceId ? `/openings/${replaceId}` : "/openings", backLabel: replaceId ? "Repertoire" : "Openings" };
  // Re-importing is done from a repertoire, and Back returns to it.
  const shell = useShell();
  useEffect(() => {
    if (replaceId) shell.setMeta(meta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replaceId, shell]);

  async function read(text: string) {
    if (!text.trim()) return;
    setBusy(true);
    setProblem("");
    setPreview(null);
    try {
      const data = await sendJson<Preview>("POST", "/api/repertoires/preview", { input: text.trim() });
      setPreview({ data, text: text.trim() });
    } catch (err) {
      if (!isSignedOut(err)) setProblem(whereText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="screen">
      <BackButton meta={meta} />
      <ScreenHead title={replaceId ? "Re-import" : "Import a repertoire"} subtitle="A Lichess study or a PGN. Every variation becomes part of the line, and the comments come with it." />
      <form
        className="card pad analyse-form"
        onSubmit={(e) => {
          e.preventDefault();
          void read(input);
        }}
      >
        <div>
          <label className="label" htmlFor="import-input">
            PGN or study link
          </label>
          <textarea
            id="import-input"
            className="field"
            rows={6}
            placeholder="https://lichess.org/study/… — or paste a PGN with variations"
            autoComplete="off"
            spellCheck={false}
            autoCapitalize="off"
            value={input}
            onChange={(e) => setInput(e.target.value)}
          />
          <p className="help">Public studies need no sign-in on Lichess. A chapter link imports just that chapter. Illegal moves are refused, with the chapter and move named.</p>
        </div>
        <div className="btn-row">
          <button type="submit" className="btn btn-primary" disabled={busy}>
            Preview
          </button>
          <label className="btn" htmlFor="import-file">
            <Icon name="upload" />
            Choose a file
          </label>
          <input
            type="file"
            accept=".pgn,text/plain,application/x-chess-pgn"
            className="sr-only"
            id="import-file"
            onChange={async (e) => {
              const f = e.target.files && e.target.files[0];
              if (!f) return;
              const text = await f.text();
              setInput(text);
              void read(text);
            }}
          />
        </div>
      </form>
      <div className="import-result">
        {busy ? <Loading text="Reading it…" /> : null}
        {problem ? <Notice kind="error">{problem}</Notice> : null}
        {preview ? <PreviewCard key={preview.text} preview={preview.data} text={preview.text} replaceId={replaceId} /> : null}
      </div>
    </div>
  );
}

function PreviewCard({ preview, text, replaceId }: { preview: Preview; text: string; replaceId: string | null }) {
  const chapters = preview.chapters || [];
  const firstName = chapters[0]?.name || "";
  // A study's chapters are often just "Chapter 1"; that is no name.
  const [name, setName] = useState(/^(chapter|chapitre|kapitel|глава)\b/i.test(firstName) ? "My repertoire" : firstName.split(":")[0]!.trim() || "My repertoire");
  const [color, setColor] = useState<"white" | "black">(preview.orientation || preview.guessed_color || "white");
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState("");

  async function save() {
    setSaving(true);
    setProblem("");
    try {
      const data = replaceId
        ? await sendJson("PUT", `/api/repertoires/${replaceId}`, { input: text })
        : await sendJson("POST", "/api/repertoires", { name: name.trim() || "My repertoire", color, input: text });
      refreshDrillBadge(true);
      // Re-imported, the repertoire it came from is the one to go back to;
      // imported, the new repertoire takes the form's place, so Back from it
      // is Openings and not the form again.
      if (replaceId) goBack(`/openings/${data.repertoire.id}`);
      else replaceWith(`/openings/${data.repertoire.id}`);
    } catch (err) {
      setSaving(false);
      if (!isSignedOut(err)) setProblem(whereText(err));
    }
  }

  return (
    <div className="card pad import-preview">
      <div className="import-summary">
        <b className="num">{chapters.length}</b> {chapters.length === 1 ? "chapter" : "chapters"} · <b className="num">{fmtCount(preview.positions)}</b> positions
      </div>
      <ol className="chapters">
        {chapters.map((c, i) => (
          <li key={i}>
            <span>{c.name || "Untitled"}</span>
            <span className="faint num">{c.moves} moves</span>
          </li>
        ))}
      </ol>
      {replaceId ? null : (
        <div className="import-fields">
          <div>
            <span className="label">You play</span>
            <Seg
              options={[
                { value: "white", label: "White" },
                { value: "black", label: "Black" },
              ]}
              value={color}
              onChange={setColor}
            />
            {preview.orientation ? null : <p className="help">Looks like a repertoire for {cap(preview.guessed_color || "white")} — right?</p>}
          </div>
          <div className="import-name">
            <label className="label" htmlFor="import-name">
              Name
            </label>
            <input id="import-name" className="field" type="text" autoComplete="off" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
        </div>
      )}
      <div className="btn-row" style={{ marginTop: "16px" }}>
        <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void save()}>
          {replaceId ? "Replace the lines" : "Save repertoire"}
        </button>
      </div>
      {saving ? <Loading text="Saving…" /> : null}
      {problem ? <Notice kind="error">{problem}</Notice> : null}
    </div>
  );
}
